import { useMemo, useRef, useState, useEffect, useCallback } from 'react';

/* CalendarView — monday.com-style month calendar, converted to TypeScript.
 * Places each item on its config.due date as a status-coloured chip; a day with
 * more than MAX_CHIPS items collapses the rest into "+k more" (click to expand);
 * items without a due date drop into an "unscheduled" strip. The month grid is an
 * accessible role="grid" with roving-tabindex arrow-key navigation. */

// ---- Inlined boardModel utilities ----

type FieldAccessor<T> = string | ((item: T) => unknown) | null | undefined;

function field<T>(item: T, keyOrFn: FieldAccessor<T>): unknown {
  if (item == null || keyOrFn == null) return undefined;
  if (typeof keyOrFn === 'function') {
    try { return (keyOrFn as (item: T) => unknown)(item); } catch { return undefined; }
  }
  return (item as Record<string, unknown>)[keyOrFn as string];
}

function parseDate(v: unknown): Date | null {
  if (v == null || v === '') return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  if (typeof v === 'number') {
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof v === 'string') {
    const s = v.trim();
    if (!s) return null;
    if (/^\d{10,}$/.test(s)) {
      const d = new Date(Number(s));
      return isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function dayKey(date: Date | string | number | null | undefined): string | null {
  const d = date instanceof Date ? date : parseDate(date);
  if (!d || isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// ---- Calendar types ----

interface CalendarItem {
  [key: string]: unknown;
}

interface CalendarConfig<T = CalendarItem> {
  id?: FieldAccessor<T>;
  start?: FieldAccessor<T>;
  due?: FieldAccessor<T>;
  title?: FieldAccessor<T>;
  accent?: FieldAccessor<T>;
  status?: FieldAccessor<T>;
  isMilestone?: (item: T) => boolean;
}

interface CalendarEntry {
  item: CalendarItem;
  i: number;
  title: string;
  color: string;
}

interface CalendarViewProps {
  items: CalendarItem[];
  config: CalendarConfig;
  onItemClick?: (item: CalendarItem) => void;
}

// ---- Constants ----

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MAX_CHIPS = 3;

const STATUS_RULES: [RegExp, string][] = [
  [/(done|complete|closed|approv|healthy|on[\s-]?plan|on[\s-]?track|green|resolv|success|\bgo\b)/i, 'var(--ok, #2E7D5B)'],
  [/(risk|overdue|block|critical|breach|reject|escalat|\bred\b|slip|fail)/i, 'var(--risk, #B4485C)'],
  [/(watch|due[\s-]?soon|amber|pending|hold|warn|caution|late)/i, 'var(--gold, #B8873A)'],
  [/(progress|in[\s-]?progress|active|open|review|doing|info|building)/i, 'var(--navy-2, #2A4A7F)'],
  [/(cui|special|escal|purple)/i, '#6B4C9A'],
];

function itemColor(item: CalendarItem, cfg: CalendarConfig): string {
  if (cfg && cfg.accent) {
    const c = field(item, cfg.accent);
    if (c) return String(c);
  }
  const s = String(field(item, cfg && cfg.status) ?? '');
  for (const [re, v] of STATUS_RULES) if (re.test(s)) return v;
  return 'var(--navy, #12234A)';
}

const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
const addMonths = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth() + n, 1);
const sameYM = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();

export default function CalendarView({ items, config, onItemClick }: CalendarViewProps) {
  const cfg = config || {};
  const click = typeof onItemClick === 'function' ? onItemClick : () => {};
  const today = new Date();
  const todayKey = dayKey(today);

  const { byDay, unscheduled } = useMemo(() => {
    const map = new Map<string, CalendarEntry[]>();
    const un: CalendarEntry[] = [];
    (items || []).forEach((item, i) => {
      const title = String(field(item, cfg.title) ?? `Item ${i + 1}`);
      const color = itemColor(item, cfg);
      const due = parseDate(field(item, cfg.due));
      const entry: CalendarEntry = { item, i, title, color };
      if (!due) { un.push(entry); return; }
      const k = dayKey(due);
      if (!k) { un.push(entry); return; }
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(entry);
    });
    return { byDay: map, unscheduled: un };
  }, [items, cfg.title, cfg.due, cfg.status, cfg.accent]);

  const [cursor, setCursor] = useState(() => startOfMonth(today));
  const [expanded, setExpanded] = useState<string | null>(null);
  const [focusIdx, setFocusIdx] = useState<number | null>(null);
  const cellRefs = useRef<(HTMLDivElement | null)[]>([]);
  const shouldFocus = useRef(false);

  const cells = useMemo(() => {
    const first = startOfMonth(cursor);
    const grid = new Date(first);
    grid.setDate(grid.getDate() - grid.getDay());
    const out: Date[] = [];
    for (let n = 0; n < 42; n++) {
      const d = new Date(grid);
      d.setDate(grid.getDate() + n);
      out.push(d);
    }
    return out;
  }, [cursor]);

  useEffect(() => {
    if (shouldFocus.current && focusIdx != null && cellRefs.current[focusIdx]) {
      cellRefs.current[focusIdx]!.focus();
      shouldFocus.current = false;
    }
  }, [focusIdx, cursor]);

  const gotoMonth = useCallback((delta: number) => {
    setCursor((c) => addMonths(c, delta));
    setExpanded(null);
  }, []);

  const onKeyDown = useCallback((e: React.KeyboardEvent, idx: number) => {
    let next = idx;
    switch (e.key) {
      case 'ArrowLeft': next = idx - 1; break;
      case 'ArrowRight': next = idx + 1; break;
      case 'ArrowUp': next = idx - 7; break;
      case 'ArrowDown': next = idx + 7; break;
      case 'Home': next = idx - (idx % 7); break;
      case 'End': next = idx - (idx % 7) + 6; break;
      case 'PageUp': e.preventDefault(); shouldFocus.current = true; gotoMonth(-1); setFocusIdx(idx); return;
      case 'PageDown': e.preventDefault(); shouldFocus.current = true; gotoMonth(1); setFocusIdx(idx); return;
      case 'Enter':
      case ' ': {
        const k = dayKey(cells[idx]);
        const list = k ? byDay.get(k) : null;
        if (list && list.length) { e.preventDefault(); click(list[0].item); }
        return;
      }
      default: return;
    }
    if (next < 0 || next > 41) return;
    e.preventDefault();
    shouldFocus.current = true;
    setFocusIdx(next);
  }, [byDay, cells, click, gotoMonth]);

  const activeIdx = focusIdx != null
    ? focusIdx
    : cells.findIndex((d) => sameYM(d, cursor) && (dayKey(d) === todayKey || d.getDate() === 1));

  const monthLabel = `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;

  return (
    <div className="bv-cal" role="group" aria-label="Calendar view">
      <div className="bv-cal-bar">
        <button type="button" className="bv-cal-nav" aria-label="Previous month" onClick={() => gotoMonth(-1)}>‹</button>
        <button type="button" className="bv-cal-nav" aria-label="Next month" onClick={() => gotoMonth(1)}>›</button>
        <div className="bv-cal-title" aria-live="polite">{monthLabel}</div>
        <button type="button" className="bv-cal-today-btn"
          onClick={() => { setCursor(startOfMonth(new Date())); setExpanded(null); }}>Today</button>
      </div>

      <div className="bv-cal-grid" role="grid" aria-label={`Calendar, ${monthLabel}`}>
        <div className="bv-cal-weekhdr" role="row">
          {WEEKDAYS.map((w) => (
            <div key={w} className="bv-cal-weekhdr__c" role="columnheader" aria-label={w}>{w}</div>
          ))}
        </div>

        {[0, 1, 2, 3, 4, 5].map((row) => (
          <div className="bv-cal-week" role="row" key={`wk${row}`}>
            {cells.slice(row * 7, row * 7 + 7).map((d, col) => {
              const idx = row * 7 + col;
              const k = dayKey(d);
              if (!k) return null;
              const inMonth = sameYM(d, cursor);
              const isToday = k === todayKey;
              const list = byDay.get(k) || [];
              const showAll = expanded === k;
              const visible = showAll ? list : list.slice(0, MAX_CHIPS);
              const overflow = list.length - visible.length;
              const cls = 'bv-cal-day' + (inMonth ? '' : ' bv-cal-day--out') + (isToday ? ' bv-cal-day--today' : '');
              const label = `${WEEKDAYS[d.getDay()]} ${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`
                + (list.length ? `, ${list.length} item${list.length === 1 ? '' : 's'}` : '');
              return (
                <div
                  key={k}
                  ref={(el) => { cellRefs.current[idx] = el; }}
                  className={cls}
                  role="gridcell"
                  aria-label={label}
                  aria-current={isToday ? 'date' : undefined}
                  tabIndex={idx === activeIdx ? 0 : -1}
                  onKeyDown={(e) => onKeyDown(e, idx)}
                  onFocus={() => setFocusIdx(idx)}
                >
                  <span className="bv-cal-daynum" aria-hidden="true">{d.getDate()}</span>
                  {visible.map((r) => (
                    <button
                      type="button" key={`c-${r.i}`} className="bv-chip" tabIndex={-1}
                      title={r.title} aria-label={r.title}
                      onClick={(e) => { e.stopPropagation(); click(r.item); }}
                      style={{ '--chip-bg': r.color } as React.CSSProperties}>
                      <span className="bv-chip__t">{r.title}</span>
                    </button>
                  ))}
                  {overflow > 0 && (
                    <button
                      type="button" className="bv-cal-more" tabIndex={-1}
                      aria-label={`Show ${overflow} more item${overflow === 1 ? '' : 's'} on ${label}`}
                      onClick={(e) => { e.stopPropagation(); setExpanded(k); }}>
                      +{overflow} more
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {unscheduled.length > 0 && (
        <div className="bv-unscheduled">
          <div className="bv-unscheduled__hd">Unscheduled · {unscheduled.length}</div>
          <div className="bv-unscheduled__list">
            {unscheduled.map((r) => (
              <button type="button" key={`u-${r.i}`} className="bv-chip"
                title={r.title} aria-label={`${r.title}. No due date.`}
                onClick={() => click(r.item)}
                style={{ '--chip-bg': r.color } as React.CSSProperties}>
                <span className="bv-chip__t">{r.title}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
